import { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  faCalendarDay,
  faCircleCheck,
  faEye,
  faClock,
  faFileInvoiceDollar,
  faFilter,
  faListAlt,
  faMoneyBillWave,
  faStore,
  faTriangleExclamation,
} from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { useCourierPayment } from "../../hooks/Courier/useCourierPayment"
import Animate, { AnimationTypes } from "../../components/Animate/Animate"
import InputField from "../../components/InputField/InputField"
import { CourierFinancialSummary } from "../../Objects/DeliveryEntity/CourierFinancialSummary"
import { ECourierPaymentConfirmationStatus } from "../../Enum/ECourierPaymentConfirmationStatus"
import { SmallInfo } from "./Components/SmallInfo"
import { CourierPaymentStatementPupop } from "./Pupop/CourierPaymentStatementPupop"
import { MetricCard } from "./Components/MetricCard"
import { StatusBadge } from "./Components/StatusBadge"
import { CourierDeliveriesStatementPupop } from "./Pupop/CourierDeliveriesStatementPupop"

export const CourierFinancialPage = () => {
  const { t } = useTranslation("frontend")

  const { handleGetFinancialSummary, handleConfirmCourierPayment } = useCourierPayment()

  const [rows, setRows] = useState<CourierFinancialSummary[]>([])

  const [showStatementModal, setShowStatementModal] = useState(false)

  const [statementCompanyName, setStatementCompanyName] = useState<string | null>(null)

  const [selectedSummary, setSelectedSummary] = useState<CourierFinancialSummary | null>(null)

  const [startDate, setStartDate] = useState(new Date().toMonthStart().toInputDate())

  const [endDate, setEndDate] = useState(new Date().toInputDate())

  /**
   * Atualiza o resumo financeiro do entregador respeitando o período atualmente selecionado.
   *
   * @param start Data inicial opcional para sobrescrever o filtro em tela.
   * @param end Data final opcional para sobrescrever o filtro em tela.
   */
  const load = (start = startDate, end = endDate) =>
    handleGetFinancialSummary({ startDate: start, endDate: end }, setRows)

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const totals = useMemo(
    () => ({
      outstanding: rows.reduce((sum, item) => sum + item.outstandingAmount, 0),
      paid: rows.reduce((sum, item) => sum + item.paidAmount, 0),
      confirmed: rows.reduce((sum, item) => sum + item.confirmedAmount, 0),
      pending: rows.reduce((sum, item) => sum + item.pendingConfirmationAmount, 0),
      rejected: rows.reduce((sum, item) => sum + item.rejectedAmount, 0),
    }),
    [rows],
  )

  const statementRows = useMemo(
    () =>
      [...rows.flatMap((row) => row.payments)].sort(
        (a, b) => new Date(b.paymentDate).getTime() - new Date(a.paymentDate).getTime(),
      ),
    [rows],
  )

  const scopedStatementRows = useMemo(() => {
    if (!statementCompanyName) {
      return statementRows
    }

    return statementRows.filter((row) => row.companyName === statementCompanyName)
  }, [statementCompanyName, statementRows])

  /**
   * Abre o popup com as entregas usadas no cálculo financeiro da loja selecionada.
   *
   * @param summary Resumo financeiro da loja no período filtrado.
   */
  const handleOpenDeliveriesModal = (summary: CourierFinancialSummary) => {
    setSelectedSummary(summary)
  }

  return (
    <div className="space-y-4 pb-4">
      <Animate type={AnimationTypes.fadeInTraslate} duration="1s">
        <section className="space-y-4 rounded-2xl border-2 border-orange-300 bg-white p-4 shadow-sm">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h2 className="text-base font-bold text-slate-900">{t("courier_financial.title")}</h2>
              <p className="text-xs text-slate-500">{t("courier_financial.description")}</p>
            </div>
            <button
              onClick={() => {
                setStatementCompanyName(null)
                setShowStatementModal(true)
              }}
              className="inline-flex items-center gap-2 rounded-xl border border-orange-300 bg-gradient-to-r from-orange-500 to-red-500 px-4 py-2.5 text-sm font-bold text-white shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:opacity-90 hover:shadow-md"
              data-interaction-description-key="interactionDescriptions:pages.delivery.courierFinancialPage.button.verExtrato"
            >
              <FontAwesomeIcon icon={faListAlt} />
              {t("courier_financial.statement_button")}
            </button>
          </div>

          <div className="rounded-lg border-2 border-orange-300 bg-orange-50 p-4 text-xs leading-relaxed text-slate-700">
            {t("courier_financial.notice")}
          </div>

          <div className="grid gap-3 md:grid-cols-[1fr_1fr_auto]">
            <InputField
              title={t("courier_financial.start_date")}
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              placeholder={t("courier_financial.start_date")}
              icon={faCalendarDay}
              descriptionKey="interactionDescriptions:pages.delivery.courierFinancialPage.input.dataInicial"
            />

            <InputField
              title={t("courier_financial.end_date")}
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              placeholder={t("courier_financial.end_date")}
              icon={faCalendarDay}
              descriptionKey="interactionDescriptions:pages.delivery.courierFinancialPage.input.dataFinal"
            />

            <div className="flex items-end">
              <button
                onClick={() => load()}
                className="inline-flex w-full items-center justify-center gap-2 rounded-md border-2 border-red-300 bg-white px-4 py-3 text-sm font-semibold text-red-700 transition hover:bg-red-50"
                data-interaction-description-key="interactionDescriptions:pages.delivery.courierFinancialPage.button.load"
              >
                <FontAwesomeIcon icon={faFilter} />
                {t("courier_financial.filter")}
              </button>
            </div>
          </div>

          <div className="grid gap-3 md:grid-cols-5">
            <MetricCard
              title={t("courier_financial.metric_outstanding")}
              value={totals.outstanding.toMoney()}
              accent="red"
              icon={faMoneyBillWave}
            />

            <MetricCard
              title={t("courier_financial.metric_paid")}
              value={totals.paid.toMoney()}
              accent="orange"
              icon={faFileInvoiceDollar}
            />

            <MetricCard
              title={t("courier_financial.metric_confirmed")}
              value={totals.confirmed.toMoney()}
              accent="green"
              icon={faCircleCheck}
            />

            <MetricCard
              title={t("courier_financial.metric_pending")}
              value={totals.pending.toMoney()}
              accent="blue"
              icon={faClock}
            />

            <MetricCard
              title={t("courier_financial.metric_rejected")}
              value={totals.rejected.toMoney()}
              accent="slate"
              icon={faTriangleExclamation}
            />
          </div>
        </section>
      </Animate>

      <Animate type={AnimationTypes.fadeInTraslate} duration="1.5s">
        <div className="grid gap-4 xl:grid-cols-2">
          {rows.map((row) => (
            <section
              key={`${row.companyId}-${row.courierId}`}
              className="rounded-2xl border-2 border-slate-200 bg-white p-4 shadow-sm"
            >
              <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <div className="flex items-start gap-3">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-orange-100 text-orange-600">
                    <FontAwesomeIcon icon={faStore} />
                  </div>

                  <div>
                    <h3 className="text-base font-bold text-slate-900">{row.companyName}</h3>

                    <p className="text-xs text-slate-500">
                      {t("courier_financial.completed_deliveries_balance", {
                        count: row.deliveredCount,
                        amount: row.outstandingAmount.toMoney(),
                      })}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2 text-xs sm:text-sm lg:min-w-[260px]">
                  <SmallInfo label={t("courier_financial.eligible")} value={row.deliveredAmount.toMoney()} />

                  <SmallInfo label={t("courier_financial.released")} value={row.paidAmount.toMoney()} />

                  <SmallInfo label={t("courier_financial.confirmed")} value={row.confirmedAmount.toMoney()} />

                  <SmallInfo label={t("courier_financial.waiting")} value={row.pendingConfirmationAmount.toMoney()} />
                </div>
              </div>

              <div className="mt-4 flex flex-wrap justify-end gap-2">
                <button
                  onClick={() => handleOpenDeliveriesModal(row)}
                  className="inline-flex items-center gap-2 rounded-xl border border-orange-300 bg-gradient-to-r from-orange-500 to-red-500 px-4 py-2.5 text-sm font-bold text-white shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:opacity-90 hover:shadow-md"
                  data-interaction-description-key="interactionDescriptions:pages.delivery.courierFinancialPage.button.handleOpenDeliveriesModalRow"
                >
                  <FontAwesomeIcon icon={faEye} />
                  {t("courier_financial.view_deliveries")}
                </button>
              </div>

              <div className="mt-4 space-y-4">
                {row.rejectedAmount > 0 && row.outstandingAmount > 0 && (
                  <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                    <div className="flex items-start gap-3">
                      <FontAwesomeIcon icon={faTriangleExclamation} className="mt-0.5" />

                      <div>
                        <p className="font-bold">{t("courier_financial.rejected_payment_title")}</p>

                        <p className="mt-1">{t("courier_financial.rejected_payment_description")}</p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <h4 className="text-sm font-bold text-slate-800">{t("courier_financial.store_payments_title")}</h4>

                  {row.payments.length === 0 && (
                    <p className="text-sm text-slate-500">{t("courier_financial.no_payments")}</p>
                  )}

                  <div className="space-y-2">
                    {row.payments.slice(0, 3).map((payment, index) => (
                      <Animate key={payment.id} type={AnimationTypes.fadeInTraslate} duration={`${0.2 + index}s`}>
                        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm transition-all duration-200 hover:border-slate-300 hover:shadow-md">
                          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                            <div className="space-y-1">
                              <strong className="block text-base font-bold text-slate-900">
                                {payment.amount.toMoney()}
                              </strong>

                              <p className="text-xs font-medium text-slate-500">
                                {t("courier_financial.released_at", {
                                  date: payment.paymentDate.toString().toReadableDate(),
                                })}
                              </p>
                            </div>

                            <div className="flex flex-col items-start gap-2 sm:items-end">
                              <StatusBadge status={payment.confirmationStatus} />

                              {payment.confirmationStatus === ECourierPaymentConfirmationStatus.Pending && (
                                <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
                                  <button
                                    onClick={() => handleConfirmCourierPayment(payment.id, true, () => load())}
                                    className="
                                      rounded-lg border border-emerald-500 bg-emerald-50 px-3 py-2
                                      text-xs font-bold text-emerald-700
                                      transition-all duration-200
                                      hover:bg-emerald-300 hover:border-emerald-400
                                      active:scale-[0.98]
                                    "
                                    data-interaction-description-key="interactionDescriptions:pages.delivery.courierFinancialPage.button.handleConfirmCourierPaymentPaymentIdTrueLoad"
                                  >
                                    {t("courier_financial.confirm_yes")}
                                  </button>

                                  <button
                                    onClick={() => handleConfirmCourierPayment(payment.id, false, () => load())}
                                    className="
                                      rounded-lg border border-red-500 bg-red-50 px-3 py-2
                                      text-xs font-bold text-red-700
                                      transition-all duration-200
                                      hover:bg-red-300 hover:border-red-400
                                      active:scale-[0.98]
                                    "
                                    data-interaction-description-key="interactionDescriptions:pages.delivery.courierFinancialPage.button.handleConfirmCourierPaymentPaymentIdFalseLoad"
                                  >
                                    {t("courier_financial.confirm_no")}
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>

                          {payment.notes && (
                            <div className="mt-3 rounded-lg bg-slate-50 px-3 py-2">
                              <p className="text-xs leading-relaxed text-slate-600">{payment.notes}</p>
                            </div>
                          )}
                        </div>
                      </Animate>
                    ))}

                    {row.payments.length > 3 && (
                      <button
                        onClick={() => {
                          setStatementCompanyName(row.companyName)
                          setShowStatementModal(true)
                        }}
                        className="w-full rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
                        data-interaction-description-key="interactionDescriptions:pages.delivery.courierFinancialPage.button.verTodosOsPagamentosDestaLoja"
                      >
                        {t("courier_financial.view_all_store_payments")}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </section>
          ))}
        </div>
      </Animate>

      {showStatementModal && (
        <CourierPaymentStatementPupop
          rows={scopedStatementRows}
          startDate={startDate}
          endDate={endDate}
          title={
            statementCompanyName
              ? t("courier_financial.statement_store_title", { store: statementCompanyName })
              : t("courier_financial.statement_title")
          }
          onClose={() => setShowStatementModal(false)}
          onApplyFilter={() => load()}
          onChangeStartDate={setStartDate}
          onChangeEndDate={setEndDate}
        />
      )}

      {selectedSummary && (
        <CourierDeliveriesStatementPupop
          title={t("courier_financial.deliveries_title")}
          description={t("courier_financial.deliveries_statement_description", {
            startDate: startDate.toString().toReadableDate(),
            endDate: endDate.toString().toReadableDate(),
          })}
          counterpartName={selectedSummary.companyName}
          deliveries={selectedSummary.deliveries}
          onClose={() => setSelectedSummary(null)}
        />
      )}
    </div>
  )
}
